import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { Boxes, FolderTree, Plus, Sparkles } from "lucide-react";
import {
  useInventory,
  useInventoryCategories,
  useCreateInventoryCategory,
  useCreateInventoryItem,
  hasStock,
  KIND_DESCRIPTIONS,
  KIND_LABELS,
  type InventoryItem,
  type InventoryKind,
} from "@/lib/inventory";
import { useInventoryCustomFields } from "@/lib/inventory-custom-fields";
import { useTenantMe } from "@/lib/tenant-me";
import { CustomFieldInput } from "@/components/v2/CustomFieldInput";
import { formatMoney, CURRENCIES } from "@/lib/currency";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/v2/PageHeader";
import { StatCard } from "@/components/v2/StatCard";
import { DataTable, type Column } from "@/components/v2/DataTable";
import { EmptyState } from "@/components/v2/EmptyState";
import { Drawer, DrawerHeader, DrawerBody, DrawerFooter } from "@/components/v2/Drawer";
import { useToast } from "@/components/v2/Toast";

/**
 * Inventory landing — list of CATEGORIES (not individual items). Mirrors the
 * Services page UX: row click drills into `/inventory/:category` where the
 * actual item table lives. Keeps the page scannable when an operator has
 * 100+ items across a dozen categories.
 *
 * Category-first flow: the landing only creates CATEGORIES; items are added
 * from inside a category page (NewItemDrawer with the category locked).
 * Registry categories with no items yet render as zero-count rows.
 *
 * KPI strip shows tenant-wide rollups; category rows show per-category counts
 * and value.
 */

const UNCATEGORIZED = "Uncategorized";

type CategoryRow = {
  category: string;
  isUncategorized: boolean;
  count: number;
  inStock: number;
  totalValue: number;
  /** Informs the kind pill; "Empty" = registry category with no items yet. */
  kindLabel: "Plans" | "Physical" | "Mixed" | "Empty";
};

export function Inventory() {
  const [createCategoryOpen, setCreateCategoryOpen] = useState(false);
  const navigate = useNavigate();

  const overview = useInventory();
  const registeredCategories = useInventoryCategories();
  const allItems = (overview.data?.items ?? []) as InventoryItem[];

  const totalItems = allItems.length;
  const inStock = allItems.filter((i) => hasStock(i.kind) && i.qtyOnHand > 0).length;
  const totalValue = allItems.reduce(
    (sum, i) => sum + (Number(i.unitPrice ?? 0) * (hasStock(i.kind) ? i.qtyOnHand : 1)),
    0,
  );
  // Every item in a tenant shares the same currency — read off the first
  // available row instead of taking another hook dependency.
  const currency = allItems.find((i) => !!i.currency)?.currency ?? "PHP";

  const rows: CategoryRow[] = useMemo(() => {
    const byCat = new Map<string, InventoryItem[]>();
    // Registry categories first — they exist even with zero items, so the
    // category-first flow can land on an empty page that persists.
    for (const name of registeredCategories.data ?? []) {
      byCat.set(name, []);
    }
    for (const it of allItems) {
      const key = it.category?.trim() || UNCATEGORIZED;
      const bucket = byCat.get(key) ?? [];
      bucket.push(it);
      byCat.set(key, bucket);
    }
    const list: CategoryRow[] = Array.from(byCat.entries()).map(([category, items]) => {
      if (items.length === 0) {
        return {
          category,
          isUncategorized: false,
          count: 0,
          inStock: 0,
          totalValue: 0,
          kindLabel: "Empty" as const,
        };
      }
      const kinds = new Set(items.map((i) => i.kind));
      const kindLabel: CategoryRow["kindLabel"] =
        kinds.size > 1 ? "Mixed" : kinds.has("plan") ? "Plans" : "Physical";
      return {
        category,
        isUncategorized: category === UNCATEGORIZED,
        count: items.length,
        inStock: items.filter((i) => hasStock(i.kind) && i.qtyOnHand > 0).length,
        totalValue: items.reduce(
          (s, i) => s + Number(i.unitPrice ?? 0) * (hasStock(i.kind) ? i.qtyOnHand : 1),
          0,
        ),
        kindLabel,
      };
    });
    list.sort((a, b) => {
      if (a.isUncategorized) return 1;
      if (b.isUncategorized) return -1;
      return a.category.localeCompare(b.category);
    });
    return list;
  }, [allItems, registeredCategories.data]);

  const columns: Column<CategoryRow>[] = [
    {
      key: "name",
      header: "Category",
      searchAccessor: (r) => r.category,
      cell: (r) => (
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-8 w-8 rounded-md bg-primary-light text-primary inline-flex items-center justify-center shrink-0">
            <FolderTree className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <p className="font-medium text-sm truncate">{r.category}</p>
            <p className="text-xs text-muted-foreground mt-0.5 truncate">
              {r.count} item{r.count === 1 ? "" : "s"}
              {r.kindLabel === "Plans"
                ? " · plan tier options"
                : r.kindLabel === "Physical"
                  ? " · physical stock"
                  : r.kindLabel === "Mixed"
                    ? " · mixed kinds"
                    : " · add your first item"}
            </p>
          </div>
        </div>
      ),
    },
    {
      key: "kind",
      header: "Kind",
      cell: (r) => (
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${
            r.kindLabel === "Plans"
              ? "bg-primary/10 text-primary"
              : r.kindLabel === "Physical"
                ? "bg-muted text-foreground"
                : r.kindLabel === "Mixed"
                  ? "bg-status-bg-pending text-status-pending"
                  : "bg-muted/60 text-muted-foreground"
          }`}
        >
          {r.kindLabel === "Plans" ? (
            <Sparkles className="h-3 w-3" />
          ) : r.kindLabel === "Empty" ? (
            <FolderTree className="h-3 w-3" />
          ) : (
            <Boxes className="h-3 w-3" />
          )}
          {r.kindLabel}
        </span>
      ),
    },
    {
      key: "inStock",
      header: "In stock",
      align: "right",
      sortAccessor: (r) => r.inStock,
      cell: (r) =>
        r.kindLabel === "Plans" || r.kindLabel === "Empty" ? (
          <span className="text-xs text-muted-foreground italic">—</span>
        ) : (
          <span className="text-sm tabular-nums">{r.inStock}</span>
        ),
    },
    {
      key: "value",
      header: "Total value",
      align: "right",
      sortAccessor: (r) => r.totalValue,
      cell: (r) => (
        <span className="text-sm font-semibold tabular-nums">
          {formatMoney(currency, r.totalValue)}
        </span>
      ),
    },
  ];

  return (
    <div className="px-6 py-6 space-y-6">
      <PageHeader
        title="Inventory"
        subtitle="Categories of plan tiers and physical stock that power your services. Create a category, then add items inside it."
        actions={
          <Button size="sm" onClick={() => setCreateCategoryOpen(true)}>
            <Plus className="h-3.5 w-3.5 mr-1.5" /> New Category
          </Button>
        }
      />

      <div className="grid gap-4 md:grid-cols-3">
        <StatCard label="Total items" value={totalItems} hint="Plans + physical" />
        <StatCard label="In stock" value={inStock} hint="Physical items only" />
        <StatCard
          label="Total value"
          value={formatMoney(currency, totalValue, { fractionDigits: 0 })}
          hint="Stock × unit price"
        />
      </div>

      {overview.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {overview.isError && (
        <p className="text-sm text-destructive">{(overview.error as Error).message}</p>
      )}

      {overview.data && (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.category}
          onRowClick={(r) =>
            navigate(`/inventory/${encodeURIComponent(r.category)}`)
          }
          sortable
          resizable
          hideable
          searchable
          searchPlaceholder="Search categories…"
          pagination="client"
          pageSize={30}
          emptyState={
            <div className="p-8">
              <EmptyState
                icon={Boxes}
                title="No inventory yet"
                description="Start with a category (e.g. Photography Plans, Gowns, Add-ons) — then add plan tiers or physical items inside it."
                action={
                  <Button size="sm" onClick={() => setCreateCategoryOpen(true)}>
                    <Plus className="h-3.5 w-3.5 mr-1.5" /> Create your first category
                  </Button>
                }
              />
            </div>
          }
        />
      )}

      <NewCategoryDrawer
        open={createCategoryOpen}
        onClose={() => setCreateCategoryOpen(false)}
        onCreated={(name) => {
          setCreateCategoryOpen(false);
          navigate(`/inventory/${encodeURIComponent(name)}`);
        }}
      />
    </div>
  );
}

const newCategorySchema = z.object({
  name: z
    .string()
    .min(2, "Name too short")
    .max(100, "Max 100 characters")
    .refine((v) => v.trim().toLowerCase() !== "uncategorized", {
      message: '"Uncategorized" is reserved',
    }),
});
type NewCategoryValues = z.infer<typeof newCategorySchema>;

/**
 * Category-first entry point: creates the category record, then the caller
 * navigates into its (empty) page where items are added with the category
 * locked.
 */
function NewCategoryDrawer({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (name: string) => void;
}) {
  const toast = useToast();
  const create = useCreateInventoryCategory();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<NewCategoryValues>({ resolver: zodResolver(newCategorySchema) });

  const submit = (v: NewCategoryValues) => {
    const name = v.name.trim();
    create.mutate(name, {
      onSuccess: (cat) => {
        reset();
        toast.success("Category created", `Now add items to ${cat.name}.`);
        onCreated(cat.name);
      },
      onError: (e) => toast.error("Couldn't create category", (e as Error).message),
    });
  };

  return (
    <Drawer open={open} onClose={onClose} width="md">
      <DrawerHeader
        title="New Category"
        subtitle="Items live inside categories — create the category first, then add items on its page."
        onClose={onClose}
      />
      <DrawerBody className="px-5 py-4">
        <form id="new-category-form" onSubmit={handleSubmit(submit)} className="space-y-4">
          <div className="grid gap-1.5">
            <Label htmlFor="category-name">Name</Label>
            <Input
              id="category-name"
              placeholder="Photography Plans, Gowns, Add-ons…"
              autoFocus
              {...register("name")}
            />
            {errors.name && (
              <p className="text-xs text-destructive">{errors.name.message}</p>
            )}
          </div>
        </form>
      </DrawerBody>
      <DrawerFooter>
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="new-category-form"
          size="sm"
          disabled={isSubmitting || create.isPending}
        >
          {create.isPending ? "Creating…" : "Create Category"}
        </Button>
      </DrawerFooter>
    </Drawer>
  );
}

const newItemSchema = z.object({
  kind: z.enum(["plan", "physical", "rental", "addon"]),
  name: z.string().min(2, "Name too short"),
  // Coerce a blank SKU to undefined so it's omitted from the payload — the
  // backend treats a missing SKU as NULL (UNIQUE(tenant_id, sku) allows many
  // NULLs but not many empty strings).
  sku: z.string().trim().optional().transform((v) => v || undefined),
  description: z.string().optional(),
  unitPrice: z.coerce.number().min(0).optional(),
  currency: z.string().length(3).optional(),
  qtyOnHand: z.coerce.number().int().min(0).optional(),
});
type NewItemValues = z.infer<typeof newItemSchema>;

/**
 * Opened from a category drill-in page — the item always belongs to that
 * page's category (`category` prop, shown read-only). Currency defaults to
 * the workspace currency from Workspace → Regional.
 */
export function NewItemDrawer({
  open,
  onClose,
  category,
}: {
  open: boolean;
  onClose: () => void;
  category: string;
}) {
  const toast = useToast();
  const create = useCreateInventoryItem();
  const customFields = useInventoryCustomFields();
  const tenantMe = useTenantMe();
  const workspaceCurrency = tenantMe.data?.tenant?.currency ?? "PHP";
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<NewItemValues>({
    resolver: zodResolver(newItemSchema),
    defaultValues: {
      kind: "physical",
      qtyOnHand: 1,
    },
  });
  const kind = watch("kind");
  const chosenCurrency = watch("currency");

  // Tenant-defined inventory custom fields, filtered to those that
  // apply to this page's category. Mirrors the same scope rule the
  // backend uses: empty `scopeCategories` = applies to every item;
  // otherwise the item's category must be in the list.
  const applicableFields = useMemo(() => {
    const all = customFields.data?.fields ?? [];
    const cat = category.trim();
    return all.filter((f) => {
      const scope = f.scopeCategories ?? [];
      if (scope.length === 0) return true;
      return cat ? scope.includes(cat) : false;
    });
  }, [customFields.data, category]);

  const [fieldDraft, setFieldDraft] = useState<Record<string, string>>({});
  const setFieldValue = (fieldId: string, value: string) =>
    setFieldDraft((prev) => ({ ...prev, [fieldId]: value }));

  const submit = (v: NewItemValues) => {
    const customFieldValues = applicableFields
      .map((f) => ({
        fieldId: f.id,
        value: fieldDraft[f.id]?.trim() || null,
      }))
      .filter((row) => row.value !== null);

    create.mutate(
      {
        ...v,
        category,
        currency: v.currency ?? workspaceCurrency,
        qtyOnHand: hasStock(v.kind) ? v.qtyOnHand : undefined,
        customFieldValues: customFieldValues.length ? customFieldValues : undefined,
      },
      {
        onSuccess: () => {
          reset({
            kind: v.kind,
            currency: v.currency,
            qtyOnHand: 1,
          });
          setFieldDraft({});
          onClose();
          toast.success("Item added", `${v.name} is in inventory.`);
        },
        onError: (e) => toast.error("Couldn't add item", (e as Error).message),
      },
    );
  };

  return (
    <Drawer open={open} onClose={onClose} width="md">
      <DrawerHeader
        title="Add Inventory Item"
        subtitle={`In ${category} — plan tiers drive service pricing; physical items track stock.`}
        onClose={onClose}
      />

      <DrawerBody className="px-5 py-4">
        <form id="new-item-form" onSubmit={handleSubmit(submit)} className="space-y-5">
          <div className="grid gap-2">
            <Label>Kind</Label>
            <div className="grid grid-cols-2 gap-2">
              {(["plan", "physical", "rental", "addon"] as InventoryKind[]).map((k) => {
                const active = kind === k;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setValue("kind", k, { shouldValidate: true })}
                    className={`rounded-md border p-3 text-left transition-colors ${
                      active
                        ? "border-primary bg-primary-light"
                        : "border-border hover:bg-muted/40"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {k === "plan" ? (
                        <Sparkles className="h-4 w-4 text-primary" />
                      ) : (
                        <Boxes className="h-4 w-4 text-foreground/70" />
                      )}
                      <p className="text-sm font-medium">{KIND_LABELS[k]}</p>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {KIND_DESCRIPTIONS[k]}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              placeholder={
                kind === "plan"
                  ? "Medium plan"
                  : kind === "addon"
                    ? "Service fee"
                    : "Wedding gown — Lace"
              }
              {...register("name")}
            />
            {errors.name && (
              <p className="text-xs text-destructive">{errors.name.message}</p>
            )}
          </div>

          <div className="grid gap-1.5">
            <Label>Category</Label>
            <Input value={category} disabled />
            <p className="text-[11px] text-muted-foreground">
              Items are added from inside their category page.
            </p>
          </div>

          {hasStock(kind) && (
            <div className="grid gap-1.5">
              <Label htmlFor="sku">
                SKU <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <Input id="sku" placeholder="Optional — e.g. GWN-LCE-002" {...register("sku")} />
            </div>
          )}

          <div className="grid gap-1.5">
            <Label htmlFor="description">Description</Label>
            <textarea
              id="description"
              rows={2}
              {...register("description")}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="unitPrice">Price</Label>
              <Input
                id="unitPrice"
                type="number"
                step="0.01"
                placeholder="0.00"
                {...register("unitPrice")}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="currency">Currency</Label>
              <Select
                value={chosenCurrency ?? workspaceCurrency}
                onValueChange={(v) => setValue("currency", v, { shouldValidate: true })}
              >
                <SelectTrigger id="currency" className="h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c.iso} value={c.iso}>
                      <span className="inline-flex items-center gap-2">
                        <span className="font-semibold w-5 text-center">{c.symbol}</span>
                        <span>{c.name}</span>
                        <span className="text-muted-foreground text-xs">({c.iso})</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {hasStock(kind) && (
            <div className="grid gap-1.5">
              <Label htmlFor="qtyOnHand">Quantity on hand</Label>
              <Input
                id="qtyOnHand"
                type="number"
                min="0"
                {...register("qtyOnHand")}
              />
            </div>
          )}

          {applicableFields.length > 0 && (
            <div className="border-t border-border pt-4 space-y-3">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Custom fields
              </p>
              {applicableFields.map((f) => (
                <div key={f.id} className="grid gap-1.5">
                  <Label htmlFor={`cf-${f.id}`}>
                    {f.label}
                    {f.required && <span className="text-destructive"> *</span>}
                  </Label>
                  <CustomFieldInput
                    field={{ type: f.type, options: f.options, required: f.required }}
                    value={fieldDraft[f.id] ?? ""}
                    onChange={(v) => setFieldValue(f.id, v)}
                  />
                </div>
              ))}
            </div>
          )}
        </form>
      </DrawerBody>

      <DrawerFooter>
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="new-item-form"
          size="sm"
          disabled={isSubmitting || create.isPending}
        >
          {create.isPending ? "Adding…" : "Add Item"}
        </Button>
      </DrawerFooter>
    </Drawer>
  );
}
